import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { ScrollArea } from "./ui/scroll-area";
import DatePicker from "./DatePicker";
import { Loader2, Lock, CheckCircle2, ShieldCheck, FileSignature, ChevronLeft, ChevronRight } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { format } from "date-fns";

const DEFAULT_FORM = {
 // Identity
 full_name: "", preferred_name: "", pronouns: "", dob: "",
 sex_assigned_at_birth: "", gender_identity: "",
 email: "", phone: "",
 address_line1: "", address_line2: "", city: "", state: "MD", zip_code: "",
 // Emergency contact
 emergency_name: "", emergency_relationship: "", emergency_phone: "",
 // Presenting
 chief_complaint: "", symptoms_started: "", impact_on_life: "",
 prior_treatment: "", prior_hospitalizations: "",
 // Psychiatric history
 previous_diagnoses: "", family_psych_history: "",
 // Medical
 current_medications: "", allergies: "", medical_conditions: "", primary_care_provider: "",
 // Substance use
 alcohol_use: "", tobacco_use: "", recreational_drug_use: "", caffeine_use: "",
 // Social
 living_situation: "", employment: "", education: "", marital_status: "", support_system: "",
 // Safety
 suicidal_ideation_history: "", homicidal_ideation_history: "", self_harm_history: "",
 // Consents
 consent_to_treat: false, consent_telehealth: false, consent_hipaa_ack: false,
 consent_release_of_info: false, consent_financial_responsibility: false,
 // Signature
 signature_typed: "", signature_date: format(new Date(), "yyyy-MM-dd"),
 notes: "",
};

const STEPS = [
 { id: "patient", title: "Patient details", subtitle: "Tell us a bit about yourself." },
 { id: "presenting", title: "What brings you in", subtitle: "Help us understand what you're experiencing." },
 { id: "history", title: "Medical & psychiatric history" },
 { id: "lifestyle", title: "Lifestyle & support" },
 { id: "safety", title: "Safety screening", subtitle: "Honest answers help us care for you safely." },
 { id: "consent", title: "Consent & signature", subtitle: "Required to receive care." },
];

function Section({ children }) { return <div className="space-y-5">{children}</div>; }
function Field({ label, optional, htmlFor, children, hint, span = "" }) {
 return (
 <div className={span}>
 <Label htmlFor={htmlFor} className="text-sm">{label} {optional && <span className="text-muted_text font-normal">(optional)</span>}</Label>
 <div className="mt-1.5">{children}</div>
 {hint && <p className="text-xs text-muted_text mt-1">{hint}</p>}
 </div>
 );
}

export default function IntakeFormDialog({ open, onOpenChange, appointment, prefill }) {
 const [step, setStep] = useState(0);
 const [form, setForm] = useState(DEFAULT_FORM);
 const [submitting, setSubmitting] = useState(false);
 const [done, setDone] = useState(false);

 useEffect(() => {
 if (open) {
 // Pre-fill from booked appointment when available
 setForm((f) => ({
 ...DEFAULT_FORM,
 full_name: appointment?.full_name || prefill?.full_name || "",
 email: appointment?.email || prefill?.email || "",
 phone: appointment?.phone || prefill?.phone || "",
 dob: appointment?.insurance?.dob || "",
 }));
 setStep(0);
 setDone(false);
 }
 }, [open, appointment, prefill]);

 function set(key, val) { setForm((f) => ({ ...f, [key]: val })); }

 function validateCurrent() {
 if (step === 0) {
 if (!form.full_name || !form.email || !form.phone) { toast.error("Please complete name, email, and phone."); return false; }
 }
 if (step === STEPS.length - 1) {
 if (!form.consent_to_treat || !form.consent_telehealth || !form.consent_hipaa_ack) {
 toast.error("Please review and accept the required consents.");
 return false;
 }
 if (!form.signature_typed || form.signature_typed.trim().length < 2) {
 toast.error("Please type your full name as your signature.");
 return false;
 }
 if (!form.signature_date) { toast.error("Please confirm the signature date."); return false; }
 }
 return true;
 }

 function next() { if (validateCurrent()) setStep((s) => Math.min(STEPS.length - 1, s + 1)); }
 function back() { setStep((s) => Math.max(0, s - 1)); }

 async function submit() {
 if (!validateCurrent()) return;
 setSubmitting(true);
 try {
 const payload = { ...form, appointment_id: appointment?.id || null };
 await api.post("/intake-forms", payload);
 setDone(true);
 toast.success("Intake submitted. A specialist will reach out shortly.");
 } catch (err) {
 toast.error(formatApiError(err.response?.data?.detail) || "Could not submit intake.");
 } finally {
 setSubmitting(false);
 }
 }

 const current = STEPS[step];
 const progress = Math.round(((done ? STEPS.length : step + 1) / STEPS.length) * 100);

 return (
 <Dialog open={open} onOpenChange={onOpenChange}>
 <DialogContent data-testid="intake-dialog" className="max-w-3xl p-0 gap-0 bg-cream border-[#E8EBE4] rounded-3xl overflow-hidden">
 <DialogHeader className="px-6 sm:px-8 pt-7 pb-4 border-b border-[#E8EBE4]">
 <div className="flex items-center gap-3">
 <div className="w-11 h-11 rounded-2xl bg-sage-50 text-sage-700 flex items-center justify-center shrink-0">
 <FileSignature className="w-5 h-5" strokeWidth={1.75} />
 </div>
 <div className="flex-1 min-w-0">
 <DialogTitle className="font-heading text-2xl text-ink">Intake, Assessment &amp; Consent</DialogTitle>
 <DialogDescription className="text-sm text-muted_text mt-1">
 Secure &amp; confidential. Submission is encrypted in transit and visible only to your care team.
 </DialogDescription>
 </div>
 <span className="hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-sage-500/15 text-sage-700 font-semibold">
 <Lock className="w-3 h-3" /> HIPAA-aligned
 </span>
 </div>
 <div className="mt-5">
 <div className="flex items-center justify-between text-xs text-muted_text mb-2">
 <span data-testid="intake-step-label">Step {Math.min(step + 1, STEPS.length)} of {STEPS.length} · {current.title}</span>
 <span className="tabular-nums">{progress}%</span>
 </div>
 <div className="h-1.5 rounded-full bg-paper border border-[#E8EBE4] overflow-hidden">
 <div className="h-full bg-gold-500 transition-all duration-500" style={{ width: `${progress}%` }} data-testid="intake-progress" />
 </div>
 </div>
 </DialogHeader>

 <ScrollArea className="max-h-[60vh]">
 <div className="px-6 sm:px-8 py-7">
 {done ? (
 <div data-testid="intake-success" className="py-10 text-center">
 <div className="w-14 h-14 mx-auto rounded-2xl bg-sage-500/15 text-sage-700 flex items-center justify-center">
 <CheckCircle2 className="w-7 h-7" strokeWidth={1.75} />
 </div>
 <h3 className="mt-5 font-heading text-2xl font-semibold text-ink">Thank you, your intake is in.</h3>
 <p className="mt-3 text-muted_text leading-relaxed max-w-md mx-auto">
 A specialist will review your responses and reach out shortly. We look forward to meeting you at your scheduled visit.
 </p>
 <Button onClick={() => onOpenChange(false)} data-testid="intake-close" className="btn-ghost mt-7">Close</Button>
 </div>
 ) : (
 <>
 {current.subtitle && <p className="text-sm text-muted_text mb-5">{current.subtitle}</p>}

 {step === 0 && (
 <Section>
 <div className="grid sm:grid-cols-2 gap-5">
 <Field label="Legal full name" htmlFor="if_name">
 <Input id="if_name" data-testid="intake-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="h-11" />
 </Field>
 <Field label="Preferred name" optional htmlFor="if_pref">
 <Input id="if_pref" data-testid="if-pref" value={form.preferred_name} onChange={(e) => set("preferred_name", e.target.value)} className="h-11" />
 </Field>
 <Field label="Pronouns" optional htmlFor="if_pron">
 <Input id="if_pron" data-testid="if-pron" value={form.pronouns} onChange={(e) => set("pronouns", e.target.value)} placeholder="she/her, he/him, they/them..." className="h-11" />
 </Field>
 <Field label="Date of birth">
 <DatePicker testId="if-dob" value={form.dob} onChange={(v) => set("dob", v)} placeholder="Select date of birth" maxDate={new Date()} />
 </Field>
 <Field label="Sex assigned at birth" optional htmlFor="if_sab">
 <Input id="if_sab" data-testid="if-sab" value={form.sex_assigned_at_birth} onChange={(e) => set("sex_assigned_at_birth", e.target.value)} className="h-11" />
 </Field>
 <Field label="Gender identity" optional htmlFor="if_gi">
 <Input id="if_gi" data-testid="if-gi" value={form.gender_identity} onChange={(e) => set("gender_identity", e.target.value)} className="h-11" />
 </Field>
 <Field label="Email" htmlFor="if_email">
 <Input id="if_email" type="email" data-testid="intake-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-11" />
 </Field>
 <Field label="Phone" htmlFor="if_phone">
 <Input id="if_phone" data-testid="intake-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-11" />
 </Field>
 <Field label="Address" optional htmlFor="if_addr1" span="sm:col-span-2">
 <Input id="if_addr1" data-testid="if-addr1" value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} placeholder="Street" className="h-11" />
 </Field>
 <Field label="Apt / Suite" optional htmlFor="if_addr2">
 <Input id="if_addr2" data-testid="if-addr2" value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} className="h-11" />
 </Field>
 <Field label="City" optional htmlFor="if_city">
 <Input id="if_city" data-testid="if-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="h-11" />
 </Field>
 <Field label="State" optional htmlFor="if_state">
 <Input id="if_state" data-testid="if-state" value={form.state} onChange={(e) => set("state", e.target.value)} className="h-11" />
 </Field>
 <Field label="ZIP" optional htmlFor="if_zip">
 <Input id="if_zip" data-testid="if-zip" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} className="h-11" />
 </Field>
 </div>
 <div className="pt-5 mt-2 border-t border-[#E8EBE4]">
 <p className="font-heading text-sm font-semibold text-ink mb-3">Emergency contact</p>
 <div className="grid sm:grid-cols-3 gap-5">
 <Field label="Name" optional htmlFor="if_ename">
 <Input id="if_ename" data-testid="if-ename" value={form.emergency_name} onChange={(e) => set("emergency_name", e.target.value)} className="h-11" />
 </Field>
 <Field label="Relationship" optional htmlFor="if_erel">
 <Input id="if_erel" data-testid="if-erel" value={form.emergency_relationship} onChange={(e) => set("emergency_relationship", e.target.value)} placeholder="Spouse, parent, friend..." className="h-11" />
 </Field>
 <Field label="Phone" optional htmlFor="if_ephone">
 <Input id="if_ephone" data-testid="if-ephone" value={form.emergency_phone} onChange={(e) => set("emergency_phone", e.target.value)} className="h-11" />
 </Field>
 </div>
 </div>
 </Section>
 )}

 {step === 1 && (
 <Section>
 <Field label="What brings you in today?" htmlFor="if_cc">
 <Textarea id="if_cc" data-testid="if-cc" value={form.chief_complaint} onChange={(e) => set("chief_complaint", e.target.value)} className="min-h-[120px]" placeholder="In your own words, there is no wrong answer." />
 </Field>
 <Field label="When did these concerns begin?" optional htmlFor="if_when">
 <Input id="if_when" data-testid="if-when" value={form.symptoms_started} onChange={(e) => set("symptoms_started", e.target.value)} placeholder="e.g., 6 months ago, since college, etc." className="h-11" />
 </Field>
 <Field label="How are they impacting your daily life?" optional htmlFor="if_impact">
 <Textarea id="if_impact" data-testid="if-impact" value={form.impact_on_life} onChange={(e) => set("impact_on_life", e.target.value)} className="min-h-[90px]" placeholder="Work, relationships, sleep, school, day-to-day functioning..." />
 </Field>
 <Field label="Any prior psychiatric or therapy treatment?" optional htmlFor="if_prior">
 <Textarea id="if_prior" data-testid="if-prior" value={form.prior_treatment} onChange={(e) => set("prior_treatment", e.target.value)} className="min-h-[90px]" placeholder="Providers seen, medications tried, helpful/unhelpful approaches..." />
 </Field>
 <Field label="Any prior psychiatric hospitalisations?" optional htmlFor="if_hosp">
 <Textarea id="if_hosp" data-testid="if-hosp" value={form.prior_hospitalizations} onChange={(e) => set("prior_hospitalizations", e.target.value)} className="min-h-[70px]" />
 </Field>
 </Section>
 )}

 {step === 2 && (
 <Section>
 <Field label="Previous psychiatric diagnoses" optional htmlFor="if_dx">
 <Textarea id="if_dx" data-testid="if-dx" value={form.previous_diagnoses} onChange={(e) => set("previous_diagnoses", e.target.value)} className="min-h-[80px]" />
 </Field>
 <Field label="Family mental-health history" optional htmlFor="if_fam">
 <Textarea id="if_fam" data-testid="if-fam" value={form.family_psych_history} onChange={(e) => set("family_psych_history", e.target.value)} className="min-h-[80px]" placeholder="Conditions, treatments, completed suicides if applicable..." />
 </Field>
 <Field label="Current medications & dosages" optional htmlFor="if_meds">
 <Textarea id="if_meds" data-testid="if-meds" value={form.current_medications} onChange={(e) => set("current_medications", e.target.value)} className="min-h-[90px]" placeholder="Include OTC, supplements, and as-needed meds." />
 </Field>
 <Field label="Allergies" optional htmlFor="if_allergy">
 <Textarea id="if_allergy" data-testid="if-allergy" value={form.allergies} onChange={(e) => set("allergies", e.target.value)} className="min-h-[70px]" />
 </Field>
 <Field label="Other medical conditions" optional htmlFor="if_med">
 <Textarea id="if_med" data-testid="if-med" value={form.medical_conditions} onChange={(e) => set("medical_conditions", e.target.value)} className="min-h-[70px]" />
 </Field>
 <Field label="Primary care provider" optional htmlFor="if_pcp">
 <Input id="if_pcp" data-testid="if-pcp" value={form.primary_care_provider} onChange={(e) => set("primary_care_provider", e.target.value)} placeholder="Name, practice, phone if known" className="h-11" />
 </Field>
 </Section>
 )}

 {step === 3 && (
 <Section>
 <div className="grid sm:grid-cols-2 gap-5">
 <Field label="Alcohol use" optional htmlFor="if_alc">
 <Input id="if_alc" data-testid="if-alc" value={form.alcohol_use} onChange={(e) => set("alcohol_use", e.target.value)} placeholder="Frequency, amount" className="h-11" />
 </Field>
 <Field label="Tobacco use" optional htmlFor="if_tob">
 <Input id="if_tob" data-testid="if-tob" value={form.tobacco_use} onChange={(e) => set("tobacco_use", e.target.value)} className="h-11" />
 </Field>
 <Field label="Recreational drug use" optional htmlFor="if_rec">
 <Input id="if_rec" data-testid="if-rec" value={form.recreational_drug_use} onChange={(e) => set("recreational_drug_use", e.target.value)} className="h-11" />
 </Field>
 <Field label="Caffeine" optional htmlFor="if_caf">
 <Input id="if_caf" data-testid="if-caf" value={form.caffeine_use} onChange={(e) => set("caffeine_use", e.target.value)} className="h-11" />
 </Field>
 </div>
 <Field label="Living situation" optional htmlFor="if_live">
 <Input id="if_live" data-testid="if-live" value={form.living_situation} onChange={(e) => set("living_situation", e.target.value)} placeholder="Alone, with family, etc." className="h-11" />
 </Field>
 <div className="grid sm:grid-cols-2 gap-5">
 <Field label="Employment" optional htmlFor="if_emp">
 <Input id="if_emp" data-testid="if-emp" value={form.employment} onChange={(e) => set("employment", e.target.value)} className="h-11" />
 </Field>
 <Field label="Education" optional htmlFor="if_edu">
 <Input id="if_edu" data-testid="if-edu" value={form.education} onChange={(e) => set("education", e.target.value)} className="h-11" />
 </Field>
 <Field label="Marital status" optional htmlFor="if_mar">
 <Input id="if_mar" data-testid="if-mar" value={form.marital_status} onChange={(e) => set("marital_status", e.target.value)} className="h-11" />
 </Field>
 <Field label="Support system" optional htmlFor="if_sup">
 <Input id="if_sup" data-testid="if-sup" value={form.support_system} onChange={(e) => set("support_system", e.target.value)} placeholder="Who can you rely on?" className="h-11" />
 </Field>
 </div>
 </Section>
 )}

 {step === 4 && (
 <Section>
 <div className="rounded-2xl border border-gold-500/30 bg-gold-50 p-4 text-xs text-ink/80">
 If you are in immediate danger, please call <strong>988</strong> (Suicide &amp; Crisis Lifeline) or <strong>911</strong>. Your answers here help us care for you safely, they are not a substitute for emergency response.
 </div>
 <Field label="Past or current thoughts of suicide" optional htmlFor="if_si">
 <Textarea id="if_si" data-testid="if-si" value={form.suicidal_ideation_history} onChange={(e) => set("suicidal_ideation_history", e.target.value)} className="min-h-[90px]" placeholder="Frequency, plan/intent, prior attempts..." />
 </Field>
 <Field label="Past or current thoughts of harming others" optional htmlFor="if_hi">
 <Textarea id="if_hi" data-testid="if-hi" value={form.homicidal_ideation_history} onChange={(e) => set("homicidal_ideation_history", e.target.value)} className="min-h-[80px]" />
 </Field>
 <Field label="Self-harm history" optional htmlFor="if_sh">
 <Textarea id="if_sh" data-testid="if-sh" value={form.self_harm_history} onChange={(e) => set("self_harm_history", e.target.value)} className="min-h-[80px]" />
 </Field>
 <Field label="Anything else you'd like your clinician to know?" optional htmlFor="if_notes">
 <Textarea id="if_notes" data-testid="if-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="min-h-[90px]" />
 </Field>
 </Section>
 )}

 {step === 5 && (
 <Section>
 <div className="rounded-2xl border border-[#E8EBE4] bg-paper p-5 text-sm text-ink/85 leading-relaxed">
 <div className="inline-flex items-center gap-2 text-sage-800 font-semibold mb-3"><ShieldCheck className="w-4 h-4" strokeWidth={1.75} /> Required consents</div>
 <p>Please read and confirm the following to complete your intake. Required items are marked with an asterisk (*).</p>
 </div>

 <ConsentRow
 testId="consent-treat"
 required
 checked={form.consent_to_treat}
 onChange={(v) => set("consent_to_treat", v)}
 title="Consent to Treatment & Authorisation"
 body="I voluntarily consent to psychiatric assessment, diagnosis, treatment, and medication management (when appropriate) provided by Golden Life Standard Health. I understand the risks and benefits will be discussed during my visits."
 />
 <ConsentRow
 testId="consent-telehealth"
 required
 checked={form.consent_telehealth}
 onChange={(v) => set("consent_telehealth", v)}
 title="Telehealth Consent"
 body="I consent to receive care via secure, HIPAA-aligned video visits. I understand telehealth's benefits and limitations, and that I am responsible for being in a private environment with a stable internet connection during sessions."
 />
 <ConsentRow
 testId="consent-hipaa"
 required
 checked={form.consent_hipaa_ack}
 onChange={(v) => set("consent_hipaa_ack", v)}
 title="HIPAA Privacy Acknowledgement"
 body="I acknowledge I have been offered the Notice of Privacy Practices. My protected health information will be safeguarded under HIPAA and disclosed only as permitted by law or with my consent."
 />
 <ConsentRow
 testId="consent-roi"
 checked={form.consent_release_of_info}
 onChange={(v) => set("consent_release_of_info", v)}
 title="Release of Information (optional)"
 body="I authorise my care team to communicate with my primary care provider, prior therapist, or specified family members for the purpose of coordinated care."
 />
 <ConsentRow
 testId="consent-fin"
 checked={form.consent_financial_responsibility}
 onChange={(v) => set("consent_financial_responsibility", v)}
 title="Financial Responsibility (optional)"
 body="I understand that I am responsible for any copays, deductibles, or self-pay charges not covered by insurance, and that no-show fees may apply per the practice policy."
 />

 <div className="pt-2 grid sm:grid-cols-2 gap-5">
 <Field label="Signature (type your full legal name)" htmlFor="if_sig">
 <Input id="if_sig" data-testid="intake-signature-typed" value={form.signature_typed} onChange={(e) => set("signature_typed", e.target.value)} className="h-11 font-heading italic" placeholder="Your full legal name" />
 </Field>
 <Field label="Date">
 <DatePicker testId="intake-signature-date" value={form.signature_date} onChange={(v) => set("signature_date", v)} />
 </Field>
 </div>
 </Section>
 )}
 </>
 )}
 </div>
 </ScrollArea>

 {!done && (
 <div className="border-t border-[#E8EBE4] bg-paper px-6 sm:px-8 py-4 flex items-center justify-between gap-3">
 <Button variant="outline" onClick={back} disabled={step === 0 || submitting} data-testid="intake-back" className="!rounded-full !h-11">
 <ChevronLeft className="w-4 h-4 mr-1" /> Back
 </Button>
 {step < STEPS.length - 1 ? (
 <Button onClick={next} disabled={submitting} data-testid="intake-next-btn" className="btn-primary !rounded-full !h-11 !px-6 !text-sm">
 Continue <ChevronRight className="w-4 h-4 ml-1" />
 </Button>
 ) : (
 <Button onClick={submit} disabled={submitting} data-testid="intake-submit-btn" className="btn-gold !rounded-full !h-11 !px-6 !text-sm !font-semibold">
 {submitting ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Submitting...</>) : "Submit intake"}
 </Button>
 )}
 </div>
 )}
 </DialogContent>
 </Dialog>
 );
}

function ConsentRow({ testId, required, checked, onChange, title, body }) {
 return (
 <label data-testid={testId} className="flex items-start gap-4 rounded-2xl border border-[#E8EBE4] bg-paper p-5 cursor-pointer hover:border-sage-400 transition">
 <Checkbox checked={checked} onCheckedChange={onChange} data-testid={`${testId}-cb`} className="mt-0.5" />
 <div className="flex-1">
 <p className="font-heading text-sm font-semibold text-ink">
 {title}{required && <span className="text-gold-600">*</span>}
 </p>
 <p className="mt-1.5 text-xs text-muted_text leading-relaxed">{body}</p>
 </div>
 </label>
 );
}
